<template>
  <ModuleContainer v-bind="bindModule">
    <template #btn>
      <MapControlButton
        icon="mdi-information-outline"
        @click="onIdentify"
        :active="isUseClick"
        :title="$t('map.identify.title')"
      />
    </template>
    <template #draggable="props">
      <DraggablePopup
        v-bind="props"
        :show.sync="show"
        :title="$t('map.identify.title')"
        :top="140"
        :width="400"
        :height="500"
        :right="60"
        disable-close
      >
        <template #extra-btn>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" small>
                <v-icon small>mdi-help-circle-outline </v-icon>
              </v-btn>
            </template>
            Nhấp chuột trên bản đồ để xác định dữ liệu. <br />
            Giữ <code>Shift</code> + di chuột để chọn vùng dữ liệu xác định
          </v-tooltip>
        </template>
        <div class="full-height full-width d-flex flex-column overflow-hidden">
          <div class="d-flex pa-2 flex-grow-0">
            <b class="mr-2">{{ $t('map.identify.point') }}:</b>
            <span v-html="currentPoint"> </span>
            <v-spacer />
          </div>
          <v-divider class="flex-grow-0" />
          <div class="flex-grow-0 pa-1 pt-2">
            <v-select
              hide-details
              :items="layers"
              dense
              label="Lớp bản đồ"
              outlined
              return-object
              item-text="name"
              clearable
              v-model="form.layer"
            ></v-select>
          </div>
          <div class="flex-grow-1 overflow-y-auto">
            <div
              v-if="result.loading"
              class="d-flex align-center justify-center full-height"
            >
              <v-icon> mdi-spin mdi-loading </v-icon>
            </div>
            <template v-else>
              <div
                v-if="result.items.length < 1"
                class="d-flex align-center justify-center flex-column full-height"
              >
                <v-icon size="32" color="#DADCE0">
                  mdi-layers-off-outline
                </v-icon>
                <div class="mb-1">
                  {{ $t('map.no-data') }}
                </div>
                <div style="color: gray"
                  >Nhấp chuột vào đối tượng cần xem thông tin</div
                >
                <div style="color: gray">
                  Giữ <code>Shift</code> + di chuột để chọn vùng dữ liệu xác
                  định
                </div>
              </div>

              <div class="pa-2" v-else>
                <v-list-item
                  class="px-0 elevation-1 mt-2"
                  v-for="(item, index) in result.items"
                  :key="index"
                >
                  <v-list-item-content class="pa-3">
                    <v-layout align-center class="full-width">
                      <div class="pr-3 full-width">
                        <div :title="item.name" class="d-flex align-center">
                          <div
                            class="text-truncate font-weight-bold text-subtitle-1"
                          >
                            {{ item.name || 'Không tên' }}
                          </div>
                          <v-spacer />

                          <v-btn
                            small
                            title="Bay tới"
                            icon
                            v-if="item.geometry"
                            @click.stop="
                              fitBounds(item.geometry)
                              onSetHightLight(item.geometry)
                            "
                          >
                            <v-icon small>mdi-crosshairs-gps</v-icon>
                          </v-btn>
                          <v-btn small icon @click="onSelectFeature(item)">
                            <v-icon small>mdi-information-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </v-layout>
                  </v-list-item-content>
                </v-list-item>
              </div>
            </template>
          </div>
        </div>
      </DraggablePopup>
    </template>
    <slot />
  </ModuleContainer>
</template>

<script>
import {
  get,
  merge,
} from 'lodash'
import proj4 from 'proj4'

import sdk from '@/src/service/auth-sdk'
import { LAYER_TYPE } from '@/src/views/map/layer-control/model/layer'
import { DraggablePopup } from '@components/draggable'
import MapControlButton from '@components/Map/control/MapControlButton.vue'
import ModuleMixin from '@components/Map/mixins/ModuleMixin'
import {
  bbox,
  buffer as bufferTurf,
  point as pointTurf,
} from '@turf/turf'
import {
  latDMS,
  lngDMS,
} from '@utils/CRS'

import { startBoxRangerMap } from './map-ranger'

export default {
  components: { MapControlButton, DraggablePopup },
  mixins: [ModuleMixin],
  props: { layers: { type: Array } },
  data: (vm) => ({
    isUseClick: false,
    show: false,
    origin: { latitude: 0, longitude: 0 },
    currentPoint: undefined,
    lngLat: { latitude: 0, longitude: 0 },
    form: { crs: 'EPSG:4326', layer: null },
    result: { loading: false, items: [] },
  }),

  computed: {
    ids() {
      return this.layers.reduce((acc, x) => {
        acc = acc.concat(x.layer_ids || [])
        return acc
      }, [])
    },
  },
  methods: {
    onSetHightLight(geometry) {
      if (!geometry) {
        this.$root.$emit('layer-control:hightlight-layer', {
          type: 'FeatureCollection',
          features: [],
        })
        return
      }
      this.$root.$emit('layer-control:hightlight-layer', {
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry, properties: {} }],
      })
    },
    onIdentify() {
      this.isUseClick = !this.isUseClick
      this.show = this.isUseClick
      if (this.isUseClick) {
        this.onAddIdenity()
      } else {
        this.onRemoveIdenity()
        this.onSetHightLight()
      }
    },
    onStartRanger() {
      this.map_select_ranger = startBoxRangerMap(this.map, (bbox) => {
        if (!bbox) return
        let first_point = this.map.unproject([bbox[0].x, bbox[0].y])
        let last_point = this.map.unproject([bbox[1].x, bbox[1].y])
        let form = this.form
        if (!form.layer) {
          const features = this.map.queryRenderedFeatures(bbox, {
            layers: this.ids,
          })
          form = this.getLayerInMap(features)
        }
        this.onSelectBBox(
          [first_point.lng, first_point.lat, last_point.lng, last_point.lat],
          form
        )

        // this.onSelectFeatures(features)
      })
    },
    onClose() {
      this.isUseClick = false
      this.show = false
      this.onRemoveIdenity()
      this.onSetHightLight()
    },
    onDestroy() {
      this.onRemoveIdenity()
      this.onSetHightLight()
    },
    onInit() {
      this.bindOnClickLayer = this.onMapClick.bind(this)
    },
    onAddIdenity() {
      this.result.items = []
      this.onAddClick()
      this.onStartRanger()
    },
    onRemoveIdenity() {
      if (this.map_select_ranger) {
        this.map_select_ranger.destroy()
        this.map_select_ranger = null
      }
      this.onRemoveClick()
    },
    onRemoveClick() {
      this.map.getCanvas().classList.remove('pointer')
      this.map.off('click', this.bindOnClickLayer)
      this.$emit('update:is-use-click', false)
    },
    onAddClick() {
      this.map.getCanvas().classList.add('pointer')
      this.map.on('click', this.bindOnClickLayer)
      this.$emit('update:is-use-click', true)
    },
    onMapClick(e) {
      this.show = true
      let zoomLevel = this.map.getZoom()
      let point = pointTurf([e.lngLat.lng, e.lngLat.lat])
      let buffer = bufferTurf(point, this.getBufferZoom(zoomLevel) * 0.2, {
        units: 'meters',
      })

      console.log(1, this.getBufferZoom(zoomLevel), buffer, bbox(buffer))
      let form = this.form
      if (!form.layer) {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: this.ids,
        })
        form = this.getLayerInMap(features)
      }
      this.onSelectBBox(bbox(buffer), form)
      this.origin.latitude = e.lngLat.lng
      this.origin.longitude = e.lngLat.lat
      this.changeCRS()
    },
    getLayerInMap(features) {
      if (!features || features.length < 1) {
        return
      }
      let feature = features[0]
      let layer = get(feature, 'layer.metadata')
      if (!layer.layer_id || !layer.layer_type) {
        return
      }
      return {
        layer: { info: { id: layer.layer_id } },
      }
    },
    format(point) {
      if (this.form.crs !== 'EPSG:4326') {
        let temp_point = proj4(this.form.crs, [
          parseFloat(point[0]),
          parseFloat(point[1]),
        ])
        point[0] = temp_point[1].toFixed(0)
        point[1] = temp_point[0].toFixed(0)
      } else {
        point[0] = Number.parseFloat(point[0]).toFixed(4)
        point[1] = Number.parseFloat(point[1]).toFixed(4)
      }
      return point
    },
    changePixelValue() {
      if (this.isDMS)
        this.currentPoint =
          latDMS(+this.lngLat.latitude) +
          ', &nbsp;' +
          lngDMS(+this.lngLat.longitude)
      else
        this.currentPoint =
          this.lngLat.longitude + ',&nbsp;&nbsp;' + this.lngLat.latitude
    },
    changeCRS() {
      let point = this.format([this.origin.latitude, this.origin.longitude])
      this.lngLat.latitude = point[1]
      this.lngLat.longitude = point[0]
      this.changePixelValue()
    },
    async onSelectBBox(bbox, form = {}) {
      if (!form || !form.layer || !form.layer.info || !form.layer.info.id) {
        return
      }
      this.result.loading = true
      try {
        let res = await sdk.request({
          url: 'map/identify',
          method: 'post',
          data: {
            data_type: LAYER_TYPE.NBDS_VECTO_LAYER,
            data_id: get(form, 'layer.info.id'),
            bbox,
          },
        })
        this.result.items = res.data.map((x) => ({ ...x, layer: form.layer }))
      } finally {
        this.result.loading = false
      }
    },
    async onSelectFeature(item) {
      this.result.loading = true
      try {
        let res = await sdk.request({
          url: 'map/detail',
          method: 'post',
          data: {
            data_type: LAYER_TYPE.NBDS_VECTO_LAYER,
            data_id: get(item, 'layer.info.id'),
            feature_id: item.id,
          },
        })
        this.$emit('click:select', res.data)
      } finally {
        this.result.loading = false
      }
    },
    getBufferZoom(zoomLevel) {
      if (zoomLevel <= 4) {
        return 300000
      }
      if (zoomLevel > 4 && zoomLevel <= 5) {
        return 90000
      }
      if (zoomLevel > 5 && zoomLevel <= 6) {
        return 40000
      }
      if (zoomLevel > 6 && zoomLevel <= 7) {
        return 20000
      }
      if (zoomLevel > 7 && zoomLevel <= 8) {
        return 10000
      }
      if (zoomLevel > 8 && zoomLevel <= 9) {
        return 7000
      }
      if (zoomLevel > 9 && zoomLevel <= 10) {
        return 4000
      }
      if (zoomLevel > 10 && zoomLevel <= 11) {
        return 2500
      }
      if (zoomLevel > 11 && zoomLevel <= 12) {
        return 1500
      }
      if (zoomLevel > 12 && zoomLevel <= 13) {
        return 900
      }
      if (zoomLevel > 13 && zoomLevel <= 14) {
        return 400
      }
      if (zoomLevel > 14 && zoomLevel <= 15) {
        return 300
      }
      if (zoomLevel > 15 && zoomLevel <= 16) {
        return 210
      }
      if (zoomLevel > 16 && zoomLevel <= 17) {
        return 150
      }
      if (zoomLevel > 17) {
        return 90
      }
      return 30
    },
  },
}
</script>

<style>
.boxdraw {
  border: 1px solid black;
}
</style>
